/**
 * Contenido del temario, cargado a demanda.
 *
 * Antes se importaba arriba, de forma estática, y eso metía TODO el temario
 * (lecciones completas, programa y proyectos) en el paquete inicial: la portada
 * importa `programApi`, que importa este módulo, así que el contenido viajaba en
 * el `index` aunque el visitante solo viera la portada. Con la importación
 * dinámica, el contenido vive en su propio trozo y solo se descarga cuando hace
 * falta de verdad: modo invitado o cuando no hay red. Por eso las funciones que
 * lo usan son asíncronas.
 */
let contenidoCache = null;

async function contenido() {
  if (contenidoCache) return contenidoCache;

  const [lecciones, programa, portfolio] = await Promise.all([
    import('../../../../content/lecciones/index.js'),
    import('../../../../content/programa/index.js'),
    import('../../../../content/portfolio/projects.js'),
  ]);

  contenidoCache = {
    LESSONS: lecciones.LESSONS,
    PROGRAMA: programa.PROGRAMA,
    PROGRAMA_META: programa.PROGRAMA_META,
    PROJECTS: portfolio.PROJECTS,
  };

  return contenidoCache;
}

const KEY_GUEST = 'academy_guest_session';
const KEY_PROGRESS = 'academy_lesson_progress';
const KEY_CODE = 'academy_exercise_code';
const KEY_SOLVED = 'academy_exercise_solved';
const KEY_PROJECTS = 'academy_project_progress';
const KEY_QUIZ = 'academy_quiz_results';

export const GUEST_USER = {
  id: 'guest-student-001',
  email: 'invitado@academy.dev',
  name: 'Alumno Invitado',
  role: 'STUDENT',
  timezone: 'America/Bogota',
  weeklyGoalMinutes: 300,
  createdAt: '2026-01-01T00:00:00.000Z',
};

function leerStorage(clave, defecto) {
  try {
    if (typeof window !== 'undefined' && window.navigator?.userAgent?.includes('jsdom')) {
      return defecto;
    }
    const raw = localStorage.getItem(clave);
    return raw ? JSON.parse(raw) : defecto;
  } catch {
    return defecto;
  }
}

function guardarStorage(clave, valor) {
  try {
    if (typeof window !== 'undefined' && window.navigator?.userAgent?.includes('jsdom')) {
      return;
    }
    localStorage.setItem(clave, JSON.stringify(valor));
  } catch (error) {
    console.warn(`No se pudo guardar en localStorage [${clave}]:`, error);
  }
}

export const offlineStore = {
  // ── Sesión Invitado ────────────────────────────────────────────────────────
  isGuest() {
    try {
      return localStorage.getItem(KEY_GUEST) === 'true';
    } catch {
      return false;
    }
  },

  setGuest(activo) {
    try {
      if (activo) {
        localStorage.setItem(KEY_GUEST, 'true');
      } else {
        localStorage.removeItem(KEY_GUEST);
      }
    } catch {
      // Sin acceso a localStorage (modo privado o entorno no compatible)
    }
  },

  getGuestUser() {
    return GUEST_USER;
  },

  // ── Progreso de Lecciones ──────────────────────────────────────────────────
  getAllProgress() {
    return leerStorage(KEY_PROGRESS, {});
  },

  getLessonProgress(lessonId) {
    const todos = this.getAllProgress();
    return todos[lessonId] ?? { status: 'PENDING', completedAt: null };
  },

  setLessonProgress(lessonId, status) {
    const todos = this.getAllProgress();
    const completedAt = status === 'COMPLETED' ? new Date().toISOString() : null;
    todos[lessonId] = { status, completedAt };
    guardarStorage(KEY_PROGRESS, todos);
    return { lessonId, status, completedAt };
  },

  // ── Código de Ejercicios ───────────────────────────────────────────────────
  getExerciseCode(exerciseId, starterCode) {
    const codigos = leerStorage(KEY_CODE, {});
    return codigos[exerciseId] ?? starterCode;
  },

  saveExerciseCode(exerciseId, code) {
    const codigos = leerStorage(KEY_CODE, {});
    codigos[exerciseId] = code;
    guardarStorage(KEY_CODE, codigos);
  },

  resetExerciseCode(exerciseId) {
    const codigos = leerStorage(KEY_CODE, {});
    delete codigos[exerciseId];
    guardarStorage(KEY_CODE, codigos);
  },

  // ── Ejercicios Resueltos ───────────────────────────────────────────────────
  isExerciseSolved(exerciseId) {
    const resueltos = leerStorage(KEY_SOLVED, {});
    return Boolean(resueltos[exerciseId]);
  },

  setExerciseSolved(exerciseId, superado = true) {
    const resueltos = leerStorage(KEY_SOLVED, {});
    resueltos[exerciseId] = superado;
    guardarStorage(KEY_SOLVED, resueltos);
  },

  getSolvedExercises(exerciseIds = []) {
    const resueltos = leerStorage(KEY_SOLVED, {});
    return exerciseIds.filter((id) => Boolean(resueltos[id]));
  },

  // ── Autoevaluación (quiz) ───────────────────────────────────────────────────
  getQuizState(lessonId) {
    const todos = leerStorage(KEY_QUIZ, {});
    return todos[lessonId] ?? [];
  },

  saveQuizState(lessonId, preguntas) {
    const todos = leerStorage(KEY_QUIZ, {});
    todos[lessonId] = preguntas;
    guardarStorage(KEY_QUIZ, todos);
  },

  // ── Proyectos ──────────────────────────────────────────────────────────────
  async getProjects() {
    const { PROJECTS } = await contenido();
    const estadosGuardados = leerStorage(KEY_PROJECTS, {});
    return PROJECTS.map((p) => {
      const guardado = estadosGuardados[p.projectKey] ?? {};
      return {
        ...p,
        status: guardado.status ?? 'NOT_STARTED',
        repoUrl: guardado.repoUrl ?? null,
        liveUrl: guardado.liveUrl ?? null,
        notes: guardado.notes ?? null,
        updatedAt: guardado.updatedAt ?? new Date().toISOString(),
      };
    });
  },

  updateProject(projectKey, datos) {
    const estados = leerStorage(KEY_PROJECTS, {});
    estados[projectKey] = {
      ...(estados[projectKey] ?? {}),
      ...datos,
      updatedAt: new Date().toISOString(),
    };
    guardarStorage(KEY_PROJECTS, estados);
    return estados[projectKey];
  },

  // ── Datos Estáticos para Frontend ──────────────────────────────────────────
  async getTemario() {
    const { LESSONS } = await contenido();
    const progreso = this.getAllProgress();
    const lessons = LESSONS.map((l) => {
      const estadoFila = progreso[l.id];
      return {
        id: l.id,
        month: l.month,
        week: l.week,
        order: l.order,
        title: l.title,
        summary: l.summary,
        estimatedMinutes: l.estimatedMinutes,
        exerciseCount: l.exercises.length,
        status: estadoFila?.status ?? 'PENDING',
        completedAt: estadoFila?.completedAt ?? null,
      };
    });

    const mesesNumeros = [...new Set(LESSONS.map((l) => l.month))].sort((a, b) => a - b);
    const months = mesesNumeros.map((m) => {
      const delMes = lessons.filter((l) => l.month === m);
      const completadas = delMes.filter((l) => l.status === 'COMPLETED').length;
      return {
        month: m,
        totalLessons: delMes.length,
        completedLessons: completadas,
        percent: delMes.length === 0 ? 0 : Math.round((completadas / delMes.length) * 100),
      };
    });

    return { lessons, months };
  },

  async getLeccion(lessonId) {
    const { LESSONS } = await contenido();
    const leccion = LESSONS.find((l) => l.id === lessonId);
    if (!leccion) return null;

    const progreso = this.getLessonProgress(lessonId);
    const indice = LESSONS.findIndex((l) => l.id === lessonId);
    const siguiente = LESSONS[indice + 1];

    return {
      id: leccion.id,
      month: leccion.month,
      week: leccion.week,
      order: leccion.order,
      title: leccion.title,
      summary: leccion.summary,
      estimatedMinutes: leccion.estimatedMinutes,
      exerciseCount: leccion.exercises.length,
      status: progreso.status,
      completedAt: progreso.completedAt,
      objectives: leccion.objectives,
      sections: leccion.sections,
      exercises: leccion.exercises,
      quiz: leccion.quiz ?? [],
      fuentes: leccion.fuentes ?? [],
      nextId: siguiente?.id ?? null,
    };
  },

  async getPrograma() {
    const { LESSONS, PROGRAMA, PROGRAMA_META } = await contenido();
    const months = PROGRAMA.map((m) => {
      const leccionesMes = LESSONS.filter((l) => l.month === m.month);
      const ejerciciosMes = leccionesMes.reduce((acc, l) => acc + l.exercises.length, 0);
      return {
        ...m,
        lessonCount: leccionesMes.length,
        exerciseCount: ejerciciosMes,
        lessons: leccionesMes.map((l) => ({
          id: l.id,
          title: l.title,
          summary: l.summary,
          week: l.week,
        })),
      };
    });

    return {
      meta: {
        ...PROGRAMA_META,
        lessonCount: LESSONS.length,
      },
      months,
    };
  },
};
